import React, { useMemo, useState } from 'react';
import '../styles/index.scss';
import { Route, BrowserRouter as Router, Switch, Redirect } from 'react-router-dom';
import { SocketProvider } from './context/socketContext';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, AppShell, Box, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { SideMenuNavbarLinks } from '../components/SideMenuNavbarLinks/SideMenuNavbarLinks';
import { UserButtonMenu } from '../components/UserButton/UserButton';
import { ProductFeedbackAction } from '../components/ProductFeedbackDrawer/ProductFeedbackDrawer';
import { ActiveProductSurveyPrompt } from '../components/ActiveProductSurveyPrompt/ActiveProductSurveyPrompt';
import { ProductSurveySessionProvider } from '../hooks/productSurveys/session';
import { AppLayout } from '../components/AppLayout/AppLayout';
import { HeaderMenu } from '../components/HeaderMenu/HeaderMenu';
import { AppLayoutContentRoutes } from '../components/AppLayoutContentRoutes/AppLayoutContentRoutes';
import RouteChangeTracker from '../components/RouteChangeTracker';
import { useTranslation } from 'react-i18next';
import { SideMenuHeader } from '../components/SideMenuFooter/SideMenuHeader';
import IntercomMessenger from '../components/Intercom/Intercom';
import { IconAlertCircle } from '@tabler/icons-react';
import { CompanyPlanStatus } from '../enum/CompanyPlanLevelEnum';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { CustomerDisplayProvider } from '../customerDisplay/CustomerDisplayProvider';

const App = () => {
  const { user, company } = useSelector((state) => state.user);
  const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(true);
  const [mobileOpened, { toggle: toggleMobile }] = useDisclosure();
  const showWarningCompanyMissedPayment = useMemo(() => {
    return company?.planStatus !== CompanyPlanStatus.ACTIVE && user?.isCompanyAdmin;
  }, [company, user]);
  const [showGlobalBannerAlert, setShowGlobalBannerAlert] = useState(true);
  const { t } = useTranslation(['GlobalBannerAlert']);

  return (
    <SocketProvider>
      <Router>
        <CustomerDisplayProvider>
        <RouteChangeTracker />
        <IntercomMessenger />
        <AppLayout
          menuCollapsedMobile={mobileOpened}
          menuCollapsedDesktop={desktopOpened}
          header={
            <>
              <HeaderMenu
                menuCollapsedMobile={mobileOpened}
                menuCollapsedDesktop={desktopOpened}
                toggleMobile={toggleMobile}
                toggleDesktop={toggleDesktop}
              />
            </>
          }
          navbar={
            <>
              <AppShell.Section>
                <SideMenuHeader />
              </AppShell.Section>
              <SideMenuNavbarLinks toggleMobile={toggleMobile} toggleDesktop={toggleDesktop} />
              <AppShell.Section style={{ flexShrink: 0 }}>
                <ProductSurveySessionProvider>
                  <ActiveProductSurveyPrompt />
                </ProductSurveySessionProvider>
                <ProductFeedbackAction key={user?._id} />
              </AppShell.Section>
              <AppShell.Section>
                <UserButtonMenu />
              </AppShell.Section>
            </>
          }
          mainContent={
            <Box pos={'relative'} data-testid="app-root">
              {showWarningCompanyMissedPayment && showGlobalBannerAlert ? (
                <Alert
                  icon={<IconAlertCircle />}
                  title={t('GlobalBannerAlert:planPaymentFailed.title')}
                  color="yellow"
                  radius="md"
                  withCloseButton
                  onClose={() => setShowGlobalBannerAlert(false)}
                  mb={10}>
                  <Text>
                    {t('GlobalBannerAlert:planPaymentFailed.description')}{' '}
                    <Link to="/settings/subscription">
                      {t('GlobalBannerAlert:planPaymentFailed.linkText')}
                    </Link>
                  </Text>
                </Alert>
              ) : null}
              <AppLayoutContentRoutes />
            </Box>
          }
        />
        </CustomerDisplayProvider>
      </Router>
    </SocketProvider>
  );
};

export default App;
